/* <rimal-floating-video>: draggable product video bubble + full-screen player.
   Markup: sections/rimal-floating-video.liquid. No dependencies. */
(() => {
  if (customElements.get('rimal-floating-video')) return;

  const CLOSED_KEY = 'rimal-fv-closed';
  const POS_KEY = 'rimal-fv-pos';
  const TAP_THRESHOLD = 6;
  const TOUCH_TAP_THRESHOLD = 12;

  const store = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  class RimalFloatingVideo extends HTMLElement {
    connectedCallback() {
      this.bubble = this.querySelector('[data-rimal-fv-bubble]');
      this.frame = this.querySelector('[data-rimal-fv-open]');
      this.preview = this.querySelector('.rimal-fv__preview');
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-rimal-fv-player]');
      this.full = this.querySelector('.rimal-fv__full');
      this.progress = this.querySelector('[data-rimal-fv-progress]');
      if (!this.bubble || !this.frame || !this.dialog || !this.full) return;

      this.closedKey = `${CLOSED_KEY}-${this.dataset.productId || 'default'}`;
      if (store.get(this.closedKey) === '1') {
        this.bubble.classList.add('is-hidden');
        return;
      }

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.restorePosition();
      this.bindDrag();
      this.bindBubble();
      this.bindPlayer();
      this.setupPreview();
      this.onResize = () => this.clampToViewport();
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.previewObserver?.disconnect();
    }

    /* ---------- preview ---------- */
    setupPreview() {
      if (!this.preview) return;
      this.preview.muted = true;
      this.preview.defaultMuted = true;
      this.preview.setAttribute('muted', '');
      const autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      if (!autoplay) return;
      if ('IntersectionObserver' in window) {
        this.previewObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.playPreview();
            else this.preview.pause();
          });
        });
        this.previewObserver.observe(this.bubble);
      } else {
        this.playPreview();
      }
    }

    playPreview() {
      if (this.dialog.open) return;
      if (this.preview.preload === 'none') this.preview.preload = 'metadata';
      const p = this.preview.play();
      if (p && p.catch) p.catch(() => {});
    }

    /* ---------- drag ---------- */
    bindDrag() {
      this.bubble.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        if (event.target.closest('[data-rimal-fv-close]')) return;
        const rect = this.bubble.getBoundingClientRect();
        this.drag = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top,
          threshold: event.pointerType === 'mouse' ? TAP_THRESHOLD : TOUCH_TAP_THRESHOLD,
          moved: false,
        };
      });

      this.bubble.addEventListener('pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        if (event.pointerType === 'mouse' && !(event.buttons & 1)) {
          this.drag = null;
          this.bubble.classList.remove('is-dragging');
          return;
        }
        const dx = event.clientX - this.drag.startX;
        const dy = event.clientY - this.drag.startY;
        if (!this.drag.moved) {
          if (Math.hypot(dx, dy) < this.drag.threshold) return;
          this.drag.moved = true;
          this.bubble.classList.add('is-dragging');
          try {
            this.bubble.setPointerCapture(event.pointerId);
          } catch (e) {
            /* pointer already released */
          }
        }
        event.preventDefault();
        this.setPosition(event.clientX - this.drag.offsetX, event.clientY - this.drag.offsetY);
      });

      const end = (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const { moved } = this.drag;
        this.drag = null;
        if (!moved) return;
        this.bubble.classList.remove('is-dragging');
        this.snapInside();
        // A drag must not end in an accidental open: swallow the click that follows it.
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 50);
      };
      this.bubble.addEventListener('pointerup', end);
      this.bubble.addEventListener('pointercancel', end);
    }

    setPosition(left, top) {
      const { innerWidth: vw, innerHeight: vh } = window;
      const w = this.bubble.offsetWidth;
      const h = this.bubble.offsetHeight;
      const gap = 8;
      left = Math.min(Math.max(left, gap), vw - w - gap);
      top = Math.min(Math.max(top, gap), vh - h - gap);
      this.bubble.style.left = `${left}px`;
      this.bubble.style.top = `${top}px`;
      this.bubble.style.right = 'auto';
      this.bubble.style.bottom = 'auto';
      this.position = { left, top };
    }

    snapInside() {
      if (!this.position) return;
      store.set(POS_KEY, JSON.stringify({ left: this.position.left / window.innerWidth, top: this.position.top / window.innerHeight }));
    }

    restorePosition() {
      const raw = store.get(POS_KEY);
      if (!raw) return;
      try {
        const { left, top } = JSON.parse(raw);
        if (typeof left === 'number' && typeof top === 'number') {
          this.setPosition(left * window.innerWidth, top * window.innerHeight);
        }
      } catch (e) {
        /* ignore bad data */
      }
    }

    clampToViewport() {
      if (!this.position) return;
      this.setPosition(this.position.left, this.position.top);
    }

    /* ---------- bubble buttons ---------- */
    bindBubble() {
      this.frame.addEventListener('click', (event) => {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this.open();
      });
      this.bubble.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      this.querySelector('[data-rimal-fv-close]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.bubble.classList.add('is-hidden');
        this.preview?.pause();
        store.set(this.closedKey, '1');
      });
    }

    /* ---------- player ---------- */
    bindPlayer() {
      this.toggleBtn = this.querySelector('[data-rimal-fv-toggle]');
      this.muteBtn = this.querySelector('[data-rimal-fv-mute]');

      this.toggleBtn?.addEventListener('click', () => {
        if (this.full.paused) this.full.play().catch(() => {});
        else this.full.pause();
      });

      this.muteBtn?.addEventListener('click', () => {
        this.full.muted = !this.full.muted;
        this.syncMute();
      });

      this.full.addEventListener('play', () => this.syncPlayState());
      this.full.addEventListener('pause', () => this.syncPlayState());
      this.full.addEventListener('timeupdate', () => {
        if (!this.progress || !this.full.duration) return;
        this.progress.style.width = `${(this.full.currentTime / this.full.duration) * 100}%`;
      });

      this.querySelector('[data-rimal-fv-dialog-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-rimal-fv-cta]')?.addEventListener('click', () => {
        this.close();
        const target =
          document.querySelector('.main-product__block-buy_buttons') ||
          document.querySelector('product-form') ||
          document.querySelector('.main-product__block-price');
        target?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
      });

      // Backdrop click: the dialog element itself receives the click, children don't bubble past .rimal-fv__player
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
    }

    open() {
      if (this.dialog.open) return;
      this.preview?.pause();
      this.classList.add('is-open');
      document.documentElement.classList.add('rimal-fv-lock');
      this.dialog.showModal();
      this.full.currentTime = 0;
      this.full.muted = false;
      this.syncMute();
      const p = this.full.play();
      if (p && p.catch) {
        p.catch(() => {
          // Autoplay with sound refused: fall back to muted playback, user can unmute
          this.full.muted = true;
          this.syncMute();
          this.full.play().catch(() => {});
        });
      }
      this.querySelector('[data-rimal-fv-dialog-close]')?.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      this.full.pause();
      this.classList.remove('is-open');
      document.documentElement.classList.remove('rimal-fv-lock');
      this.frame.focus({ preventScroll: true });
      this.playPreview();
    }

    syncPlayState() {
      const paused = this.full.paused;
      this.player?.classList.toggle('is-paused', paused);
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-label', paused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause);
      }
    }

    syncMute() {
      if (!this.muteBtn) return;
      const muted = this.full.muted;
      this.muteBtn.setAttribute('aria-pressed', String(muted));
      this.muteBtn.setAttribute('aria-label', muted ? this.muteBtn.dataset.labelUnmute : this.muteBtn.dataset.labelMute);
    }
  }

  customElements.define('rimal-floating-video', RimalFloatingVideo);
})();
